import {
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { pick } from "lodash-es";

import { publicBaseUrl } from "shared/constants";
import { createAxiosInstance } from "shared/helpers/axios";
import buildRequestOptions, {
  type StreamRequestOptions,
} from "shared/helpers/streamRequestOptions";
import { type ChartIndex } from "shared/modules/Charts/types";
import { type InstantInsightsChart } from "shared/modules/Charts/types/chartTypes";
import { type ChartLibraryRequestParams } from "shared/modules/Charts/types/data";
import {
  isChartSettingWithExportOptions,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import type {
  ChartExportParams,
  PublicInsightsUserResponse,
} from "shared/modules/Insights/types";
import { AIVisibilityProject, InstantInsightsReport } from "shared/resources";
import { type InsightsResponse } from "shared/services/api/StreamService";
import useStreamsStore from "shared/stores/streams";
import {
  type ConvertKeysToSnakeCase,
  type Maybe,
  type Nullable,
} from "shared/types";
import { type Mention } from "shared/types/mentions";

import ApiClient from "./ApiClient";
import { type InsightsServiceInterface } from "./InsightsServiceFactory";

type DashboardResponse = ConvertKeysToSnakeCase<InstantInsightsReport>;

const EMPTY_RESPONSE_HEADERS = {
  "x-total-count": 0,
  "x-total-pages": 0,
};

class PublicInsightsService
  extends ApiClient
  implements InsightsServiceInterface
{
  readonly isPublic = true;

  constructor(publicAccessKey: string) {
    const publicInstance = createAxiosInstance({
      baseURL: publicBaseUrl,
    });

    // Set the public access key header
    publicInstance.defaults.headers.common["X-Public-Access-Key"] =
      publicAccessKey;

    super("/insights", publicInstance);
  }

  chartExport(
    reportParams: ChartExportParams,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse<ArrayBuffer>> {
    return this.post("/chart_export", {
      params: reportParams,
      responseType: "arraybuffer",
      ...options,
    });
  }

  async chartsConfig(
    options?: AxiosRequestConfig<ChartLibraryRequestParams>
  ): Promise<ChartIndex> {
    const { data } = await this.get<ChartIndex>("/charts_config", options);

    return (Object.keys(data) as InstantInsightsChart[]).reduce(
      (acc, configKey) => {
        const config = data[configKey];

        acc[configKey] = {
          ...pick(config, [
            "info_tooltip",
            "hide_streams",
            "display_pagination",
            "requires_analytics_integration",
            "has_double_width",
            "chart_variants",
            "default_chart_variant",
            "settings_component",
            "metric_labels",
            "metric_tooltips",
          ]),
          label: config.label ?? "",
          ...(isChartSettingWithStreamsAndMediaTypes(config)
            ? { stream_ids: [] }
            : {}),
          ...(isChartSettingWithExportOptions(config)
            ? {
                export_options: Array.isArray(config.export_options)
                  ? config.export_options
                  : [],
              }
            : {}),
        };

        return acc;
      },
      {} as ChartIndex
    );
  }

  async dashboard(
    _id?: number,
    options?: AxiosRequestConfig<ChartLibraryRequestParams>
  ): Promise<Nullable<InstantInsightsReport>> {
    const response = await this.get<DashboardResponse>("/", options);

    const {
      ai_visibility_project: aiVisibilityProject,
      streams_data: streamsData,
      ...attributes
    } = response.data;

    useStreamsStore().$patch({
      streams: streamsData,
    });

    let aiVisibilityProjectResource: Maybe<AIVisibilityProject>;

    if (aiVisibilityProject) {
      aiVisibilityProjectResource = new AIVisibilityProject({
        ...aiVisibilityProject,
        canModify: false,
      });
    }

    // Convert the API response to an InstantInsightsReport instance
    return new InstantInsightsReport({
      ...attributes,
      ai_visibility_project: aiVisibilityProjectResource,
      canModify: false,
      streams_data: streamsData,
    });
  }

  getUser(): Promise<AxiosResponse<Nullable<PublicInsightsUserResponse>>> {
    return this.get<Nullable<PublicInsightsUserResponse>>("/user");
  }

  streamInsights(
    streamId?: string | number,
    options?: AxiosRequestConfig<ChartLibraryRequestParams>
  ): Promise<AxiosResponse<InsightsResponse>> {
    let path = "/stream";

    if (streamId) {
      path = `/stream/${streamId}`;
    }

    // Use the stream insights endpoint which provides the chart data for public dashboards
    return this.post<InsightsResponse>(path, options);
  }

  streamMentions({
    mentions,
    options,
    stream,
  }: StreamRequestOptions): Promise<AxiosResponse<Mention[]>> {
    // Use the same request options builder as streamClassic for consistency
    const requestOptions = buildRequestOptions({
      mentions,
      options,
      stream,
    });

    // Check if this is an empty result (no media/types) - indicated by empty data array and specific headers
    if (!requestOptions) {
      return Promise.resolve({
        config: {} as InternalAxiosRequestConfig,
        data: [],
        headers: EMPTY_RESPONSE_HEADERS,
        request: {},
        status: 200,
        statusText: "OK",
      } as AxiosResponse<Mention[]>);
    }

    return this.post(`/mentions/${stream.id}`, requestOptions);
  }
}

export default PublicInsightsService;
